import { AutomationTriggerType, IAvailableAutomationTrigger, IResourceSelectorAutomationTrigger } from '@ViewModels';
import { getTriggerTypeLabel, RESOURCE_SELECTOR_DISPLAY } from '../../../models/utils/automationDisplayConfig';

export interface IDropdownOption {
	value: string;
	label: string;
}

/** Preferred display order for trigger types in the filter dropdown */
const TRIGGER_TYPE_ORDER: AutomationTriggerType[] = [
	AutomationTriggerType.NewClient,
	AutomationTriggerType.NewDonor,
	AutomationTriggerType.NewLead,
	AutomationTriggerType.ResourceSelector,
	AutomationTriggerType.Tag,
	AutomationTriggerType.Manual,
	AutomationTriggerType.Texting,
	AutomationTriggerType.Meeting,
	AutomationTriggerType.EventRegistration,
	AutomationTriggerType.NewDonationTrigger,
	AutomationTriggerType.NewPolicyTrigger,
];

/** Static trigger type options (fallback while API is loading) */
export const TRIGGER_TYPE_OPTIONS: IDropdownOption[] = [
	{ value: 'all', label: 'All Triggers' },
	...TRIGGER_TYPE_ORDER.map(triggerType => ({
		value: triggerType as string,
		label: getTriggerTypeLabel(triggerType),
	})),
];

/**
 * Derives trigger type filter options from the available triggers API response.
 * Deduplicates by _type and maintains the preferred display order.
 */
export function deriveTriggerTypeOptions(availableTriggers: IAvailableAutomationTrigger[]): IDropdownOption[] {
	const uniqueTypes = new Set<string>();
	for (const item of availableTriggers) {
		if (item.trigger?._type) {
			uniqueTypes.add(item.trigger._type);
		}
	}
	// Always include Manual — manual automations have no publishedTrigger, so the API may not return it
	uniqueTypes.add(AutomationTriggerType.Manual);

	const sorted = [...uniqueTypes].sort((a, b) => {
		const indexA = TRIGGER_TYPE_ORDER.indexOf(a as AutomationTriggerType);
		const indexB = TRIGGER_TYPE_ORDER.indexOf(b as AutomationTriggerType);
		return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
	});

	return [
		{ value: 'all', label: 'All Triggers' },
		...sorted.map(type => ({
			value: type,
			label: getTriggerTypeLabel(type as AutomationTriggerType),
		})),
	];
}

/**
 * Derives resource selector filter options from the available triggers API response.
 * Filters to ResourceSelector triggers and uses the API name field for labels.
 */
export function deriveResourceSelectorOptions(availableTriggers: IAvailableAutomationTrigger[]): IDropdownOption[] {
	return availableTriggers
		.filter(
			item =>
				item.trigger?._type === AutomationTriggerType.ResourceSelector &&
				(item.trigger as IResourceSelectorAutomationTrigger).resourceSelector
		)
		.map(item => {
			const selectorId = (item.trigger as IResourceSelectorAutomationTrigger).resourceSelector!;
			const configLabel = RESOURCE_SELECTOR_DISPLAY[selectorId]?.label;
			return {
				value: selectorId as string,
				label: configLabel ?? item.name ?? String(selectorId),
			};
		});
}
